import React, { Fragment, memo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { packApi } from '@app/store';
import { ScheduleAvailabilityTypeEnum, ISchedule, OrderMode, PackDTO } from '@app/common';
import { Button, Form, Panel } from '@app/components';
import { PackForm } from './PackForm';
import { PackFormValues, usePackFormSubmit } from './pack-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { PackCard } from './PackCard';
import { InputFileRef } from '@app/components/form/InputFile';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store/slices/triptych.slice';
import type { ValeursDeCopie } from '../Duplication/cles-privees';

type Props = { copie?: ValeursDeCopie<PackFormValues> };

// Le formulaire porte le useForm : il est remonté (clé) quand on passe d'une copie
// au vierge ou d'une copie à une autre, car react-hook-form ne relit
// `defaultValues` qu'au montage.
const PackCreateForm = memo(({ copie }: Props) => {
  const toast = useToast();

  const triptych = useTriptych()

  const cardImageRef = useRef<InputFileRef>();

  const onSubmit = usePackFormSubmit();

  const [create, { isLoading, error: apiErrors }] = packApi.useCreateOnePacksMutation();

  const valeursVierges = {
    name: '',
    description: '',
    tags: [],
    accountCode: undefined,
    disabled: false,
    price: undefined,
    tax: undefined,
    takeAwayPrice: undefined,
    takeAwayTax: undefined,
    deliveryPrice: undefined,
    deliveryTax: undefined,
    onSpotPrice: undefined,
    onSpotTax: undefined,
    orderModes: Object.values(OrderMode),
    packToProductVariations: [],
    packToSteps: [],
    availabilityType: ScheduleAvailabilityTypeEnum.always,
    availabilitySchedule: {} as ISchedule,
  };

  const defaultValues = copie?.copieDe ? copie : valeursVierges;

  const formMethods = useForm<PackFormValues>({
    resolver: classValidatorResolver(PackDTO),
    defaultValues,
  });

  const onCreate = async (formData: FormData) => {
    try {
      const pack = await create(formData as any).unwrap();

      triptych.close({ mode: 'UPDATE', data: pack });

      toast.success('Votre menu a bien été ajouté.')
    } catch {
      toast.error('Une erreur est survenue lors de la création de votre menu.')
    }
  }

  return (
    <Fragment>
      <PackCard
        // l'aperçu de l'original reste affiché tant qu'aucun fichier n'est choisi
        pack={copie?.copieDe ? { images: copie.imagesDeLOriginal } : undefined}
        formMethods={formMethods}
        toggle={() => triptych.toggle()}
        onOpenImagePicker={() => cardImageRef.current?.open()} />

      <Form formMethods={formMethods} apiErrors={apiErrors}>

        <Panel.Content>

          <Panel.FormContainer>

            <PackForm ref={cardImageRef} />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              title="Valider"
              onPress={() => onSubmit(formMethods, onCreate)}
              grow
              large />
          </Panel.Actions>
        </Panel.Content>

      </Form>
    </Fragment>
  );
});

export const PackCreate = memo(() => {
  // Dupliquer : la fiche ouvre ce panneau avec les valeurs de copie dans le triptyque.
  const copie = useAppSelector(triptychSelectors.selectData) as ValeursDeCopie<PackFormValues> | undefined;

  return <PackCreateForm key={copie?.copieDe ?? 'creation'} copie={copie?.copieDe ? copie : undefined} />;
});
